// src/components/EquipmentManager/WriteReviewModal.jsx

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { 
  FaTimes, 
  FaStar, 
  FaCheckCircle,
  FaExclamationTriangle
} from 'react-icons/fa';
import { useNotification } from '../../context/NotificationContext';
import styles from './WriteReviewModal.module.css';

const WriteReviewModal = ({ 
  isOpen,
  equipment,
  onClose,
  onSubmit,
  editMode = false,
  existingReview = null
}) => {
  const { showSuccess, showError } = useNotification();
  
  const [formData, setFormData] = useState({
    rating: 0,
    title: '',
    reviewText: '',
    usageDuration: { value: '', unit: 'months' }
  });
  
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [hoverRating, setHoverRating] = useState(0);
  
  // Initialize form with existing review data if editing
  useEffect(() => {
    if (editMode && existingReview) {
      setFormData({
        rating: existingReview.rating || 0,
        title: existingReview.title || '',
        reviewText: existingReview.reviewText || '',
        usageDuration: existingReview.usageDuration || { value: '', unit: 'months' }
      });
    }
  }, [editMode, existingReview]);
  
  if (!isOpen) return null;
  
  const validateForm = () => {
    const newErrors = {};
    
    if (formData.rating === 0) {
      newErrors.rating = 'Please select a rating';
    }
    
    if (formData.title.trim().length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    }
    
    if (formData.title.trim().length > 100) {
      newErrors.title = 'Title must not exceed 100 characters';
    }
    
    if (formData.reviewText.trim().length < 20) {
      newErrors.reviewText = 'Review must be at least 20 characters';
    }
    
    if (formData.reviewText.trim().length > 2000) {
      newErrors.reviewText = 'Review must not exceed 2000 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('Please fix the errors in the form');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const reviewData = {
        rating: formData.rating,
        title: formData.title.trim(),
        reviewText: formData.reviewText.trim(),
        ...(formData.usageDuration.value && {
          usageDuration: {
            value: parseInt(formData.usageDuration.value),
            unit: formData.usageDuration.unit
          }
        })
      };
      
      await onSubmit(reviewData);
      
      showSuccess(editMode ? 'Review updated successfully!' : 'Review submitted successfully!');
      onClose();
      
      // Reset form
      setFormData({
        rating: 0,
        title: '',
        reviewText: '',
        usageDuration: { value: '', unit: 'months' }
      });
      setErrors({});
      
    } catch (error) {
      console.error('Error submitting review:', error);
      showError(error.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
    setErrors(prev => ({ ...prev, rating: undefined }));
  };
  
  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.modalTitle}>
            <FaStar className={styles.modalTitleIcon} />
            <h3>{editMode ? 'Edit Your Review' : 'Write a Review'}</h3>
          </div>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close Modal">
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Equipment Info */}
          <div className={styles.equipmentInfo}>
            <img 
              src={equipment.images?.[0]?.url || equipment.imageUrl || '/default-equipment.png'} 
              alt={equipment.equipmentName}
              className={styles.equipmentImage}
            />
            <div className={styles.equipmentDetails}>
              <h3>{equipment.equipmentName}</h3>
              <p>Share your experience with this equipment</p>
            </div>
          </div>
          
          <form onSubmit={handleSubmit} className={styles.form}>
            {/* Rating */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                Your Rating <span className={styles.required}>*</span>
              </label>
              <div className={styles.starRating}>
                {[1, 2, 3, 4, 5].map(star => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => handleRatingClick(star)}
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    className={star <= (hoverRating || formData.rating) ? styles.starFilled : styles.starEmpty}
                  >
                    <FaStar />
                  </button>
                ))}
              </div>
              {errors.rating && (
                <span className={styles.errorMessage}>
                  <FaExclamationTriangle /> {errors.rating}
                </span>
              )}
            </div>
            
            {/* Title */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reviewTitle">
                Review Title <span className={styles.required}>*</span>
              </label>
              <input
                id="reviewTitle"
                type="text"
                placeholder="Sum up your experience"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                maxLength={100}
                className={styles.formInput}
              />
              <div className={styles.charCount}>
                {formData.title.length}/100
              </div>
              {errors.title && (
                <span className={styles.errorMessage}>
                  <FaExclamationTriangle /> {errors.title}
                </span>
              )}
            </div>
            
            {/* Review Text */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel} htmlFor="reviewText">
                Your Review <span className={styles.required}>*</span>
              </label>
              <textarea
                id="reviewText"
                placeholder="Tell us about your experience with this equipment..."
                value={formData.reviewText}
                onChange={(e) => setFormData(prev => ({ ...prev, reviewText: e.target.value }))}
                maxLength={2000}
                rows={6}
                className={styles.formTextarea}
              />
              <div className={styles.charCount}>
                {formData.reviewText.length}/2000
              </div>
              {errors.reviewText && (
                <span className={styles.errorMessage}>
                  <FaExclamationTriangle /> {errors.reviewText}
                </span>
              )}
            </div>
            
            {/* Usage Duration */}
            <div className={styles.formGroup}>
              <label className={styles.formLabel}>
                How long have you used this? (Optional)
              </label>
              <div className={styles.durationInputs}>
                <input
                  type="number"
                  placeholder="0"
                  value={formData.usageDuration.value}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    usageDuration: { ...prev.usageDuration, value: e.target.value }
                  }))}
                  min="0"
                  className={styles.durationValue}
                />
                <select
                  value={formData.usageDuration.unit}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    usageDuration: { ...prev.usageDuration, unit: e.target.value }
                  }))}
                  className={styles.durationUnit}
                >
                  <option value="days">Days</option>
                  <option value="weeks">Weeks</option>
                  <option value="months">Months</option>
                  <option value="years">Years</option>
                </select>
              </div>
            </div>
          </form>
        </div>
        
        <div className={styles.modalFooter}>
          <button
            type="button"
            onClick={onClose}
            className={styles.cancelBtn}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className={styles.submitBtn}
            disabled={submitting}
          >
            {submitting ? (
              <>Submitting...</>
            ) : (
              <>
                <FaCheckCircle />
                {editMode ? 'Update Review' : 'Submit Review'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

WriteReviewModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  equipment: PropTypes.object.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  editMode: PropTypes.bool,
  existingReview: PropTypes.object
};

export default WriteReviewModal;

